import { useReducer } from 'react';

type Action =
	| {
			type: 'CREATE';
			payload: string;
	  }
	| {
			type: 'UPDATE';
			payload: {
				index: number;
				value: string;
			};
	  }
	| {
			type: 'DELETE';
			payload: number;
	  }
	| {
			type: 'OFFSET';
			payload: number;
	  }
	| {
			type: 'CLEAR';
	  };

type State = {
	offset: number;
	create: string[];
	delete: number[];
	update: Record<number, string>;
};

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'CREATE': {
			return {
				...state,
				create: [...state.create, action.payload],
			};
		}
		case 'UPDATE': {
			const { index, value } = action.payload;
			const currenState = { ...state };

			if (index < state.offset) {
				currenState.update = {
					...currenState.update,
					[index]: value,
				};
			} else {
				const localIndex = index - state.offset;
				currenState.create = currenState.create.map((item, i) =>
					i === localIndex ? value : item,
				);
			}

			return currenState;
		}
		case 'DELETE': {
			const index = action.payload;
			const localIndex = index - state.offset;
			return {
				...state,
				create:
					index >= state.offset
						? state.create.filter((_, i) => i !== localIndex)
						: state.create,
				delete: index < state.offset ? [...state.delete, index] : state.delete,
			};
		}
		case 'OFFSET': {
			return {
				...state,
				offset: action.payload,
			};
		}
		case 'CLEAR': {
			return {
				...state,
				create: [],
				update: {},
				delete: [],
			};
		}
		default: {
			return state;
		}
	}
};

export function useCRUD(offset: number) {
	const [state, dispatch] = useReducer(reducer, {
		offset,
		create: [],
		update: {},
		delete: [],
	});

	function addItem(item: string) {
		dispatch({
			type: 'CREATE',
			payload: item,
		});
	}

	function deleteItem(index: number) {
		dispatch({
			type: 'DELETE',
			payload: index,
		});
	}

	function updateItem(index: number, item: string) {
		dispatch({
			type: 'UPDATE',
			payload: {
				index,
				value: item,
			},
		});
	}

	function clear() {
		dispatch({
			type: 'CLEAR',
		});
	}

	function setOffset(index: number) {
		dispatch({
			type: 'OFFSET',
			payload: index,
		});
	}

	const isDirty =
		state.create.length > 0 ||
		state.delete.length > 0 ||
		Object.keys(state.update).length > 0;

	return {
		actions: {
			addItem,
			deleteItem,
			updateItem,
			clear,
			setOffset,
		},
		list: state.create,
		updateList: state.update,
		deleteList: state.delete,
		isDirty,
	};
}

export type CRUD = ReturnType<typeof useCRUD>;
export type CRUDActions = CRUD['actions'];
export type CRUDList = CRUD['list'];
